import axios from "../axiosInstance";

/**
 * Creates a new course
 *
 * @param { courseName } - name of the course to create
 * @returns { Object } - course information
 */
export const attempCourseCreation = async (courseName) => {
  const body = {
    name: courseName,
  };

  return await axios.post("/teachers/courses/", body).then((res) => {
    if (res.status === 201) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 *  Retrieves the list of lectures of a given course
 *
 * @param {string} courseUUID - the course uuid
 * @returns { Object } - all information about a course
 */
export const retrieveCourseInfo = async (courseUUID) => {
  return await axios.get("/teachers/courses/" + courseUUID).then((res) => {
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Retrieves the information about a lecture
 *
 * @param {string} lectureUUID - the lecture uuid
 * @returns { Object } - all information about a lecture
 */
export const retrieveLectureInfo = async (lectureUUID) => {
  return await axios.get("/teachers/lectures/" + lectureUUID).then((res) => {
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Deletes a course given its uuid
 *
 * @param {string} courseUUID - the course uuid
 */
export const attempCourseDeletion = async (courseUUID) => {
  return await axios.delete("/teachers/courses/" + courseUUID).then((res) => {
    if (res.status !== 200) {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Retrieves the list of courses taught by the user
 *
 * @returns { Array<Object> } - list of courses
 */
export const retrieveTaughtCourses = async () => {
  return await axios.get("/teachers/courses/").then((res) => {
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Creates a new live lecture
 *
 * @param {string} courseUUID - the course uuid
 * @param {Object} data - the lecture data
 * @param {string} data.name - the lecture name
 * @param {string} data.startTime - the lecture start time
 * @param {string} data.endTime - the lecture end time
 * @param {string} data.videoId - the youtube video id
 * @param {bool} data.published - true if the lecture is public
 * @returns { Object } - the lecture information
 */
export const attempLiveLectureCreation = async (courseUUID, { name, startTime, endTime, videoId, published }) => {
  const body = {
    name,
    startTime,
    endTime,
    videoId,
    published,
  };

  return await axios.post(`/teachers/courses/${courseUUID}/live`, body).then((res) => {
    if (res.status === 201) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Creates a new flipped lecture
 *
 * @param {string} courseUUID - the course uuid
 * @param {Object} data - the lecture data
 * @param {string} data.name - the lecture name
 * @param {string} data.videoId - the youtube video id
 * @param {bool} data.published - true if the lecture is public
 * @returns { Object } - the lecture information
 */
export const attempFlippedLectureCreation = async (courseUUID, { name, videoId, published }) => {
  const body = {
    name,
    videoId,
    published,
  };

  return await axios.post(`/teachers/courses/${courseUUID}/flipped`, body).then((res) => {
    if (res.status === 201) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Delete a lecture given its uuid
 *
 * @param {string} lectureUUID - the lecture uuid
 */
export const attempLectureDeletion = async (lectureUUID) => {
  return await axios.delete(`/teachers/lectures/${lectureUUID}`).then((res) => {
    if (res.status !== 200) {
      throw new Error(res.data.code);
    }
  });
};

/**
 *
 * @param {Object} data - the lecture data
 * @param {string} lectureUUID - the lecture uuid
 * @param {string} name - the name of the lecture
 * @param {boolean} published - true if the lecture is published
 * @param {string} startTime - the start time of the lecture
 * @param {string} endTime - the end time of the lecture
 * @param {string} videoId - the youtube video id of the lecture
 * @returns {Object} - the updated lecture data
 */
export const attempUpdateLecture = async ({ lectureUUID, name, published, startTime, endTime, videoId }) => {
  const body = { name, published, startTime, endTime, videoId };
  return await axios.patch(`/teachers/lectures/${lectureUUID}`, body).then((res) => {
    if (res.status === 200) {
      return res.data.data;
    } else {
      throw new Error(res.data.code);
    }
  });
};

/**
 * Upload slides in a lecture
 *
 * @param {string} lectureUUID
 *
 */
export const attempUploadSlides = async ({ slides, lectureUUID }) => {
  const formData = new FormData();
  formData.append("slides", slides);

  return await axios
    .post(`/teachers/lectures/${lectureUUID}/slides`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => {
      if (res.status !== 201) {
        throw new Error(res.formData.code);
      }
    });
};

/**
 * Delete slides in a lecture
 *
 * @param {string} lectureUUID
 */
export const attempDeleteSlides = async (lectureUUID) => {
  return await axios.delete(`/lectures/${lectureUUID}/slides`, lectureUUID).then((res) => {
    if (res.status !== 200) {
      throw new Error(res.formData.code);
    }
  });
};
